/**
 * React Query hooks for Product Library
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productLibraryService } from '../services';
import type {
  ProductSearchParams,
  CreateProductLibraryItemDto,
  UpdateProductLibraryItemDto,
  ProductCategory,
} from '../types';
import { toast } from 'sonner';

// Query keys
export const productLibraryKeys = {
  all: ['product-library'] as const,
  search: (params: ProductSearchParams) => [...productLibraryKeys.all, 'search', params] as const,
  byId: (id: string) => [...productLibraryKeys.all, 'byId', id] as const,
  byCategory: (category: ProductCategory) =>
    [...productLibraryKeys.all, 'byCategory', category] as const,
  brands: () => [...productLibraryKeys.all, 'brands'] as const,
  featured: () => [...productLibraryKeys.all, 'featured'] as const,
};

/**
 * Hook to search products
 * Runs when there's a search query OR when browsing by category/filters
 */
export function useProductSearch(params: ProductSearchParams) {
  const { enabled = true, ...searchParams } = params;
  // Enable query when there's a search term, category filter, or we just want all products
  const hasSearchQuery = !!searchParams.query && searchParams.query.length > 0;
  const hasCategoryFilter = !!searchParams.category;
  // Default to enabled (browse mode) - allows fetching all products without search
  const shouldEnable = enabled && (hasSearchQuery || hasCategoryFilter || true);

  return useQuery({
    queryKey: productLibraryKeys.search(searchParams),
    queryFn: () => productLibraryService.search(searchParams),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: shouldEnable,
  });
}

/**
 * Hook to get a single product by ID
 */
export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: productLibraryKeys.byId(id!),
    queryFn: () => productLibraryService.getById(id!),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds - shorter for icon updates to propagate quickly
  });
}

/**
 * Hook to get products by category
 */
export function useProductsByCategory(category: ProductCategory | undefined, limit?: number) {
  return useQuery({
    queryKey: productLibraryKeys.byCategory(category || 'unknown'),
    queryFn: () => productLibraryService.getByCategory(category!, limit),
    staleTime: 5 * 60 * 1000,
    enabled: !!category,
  });
}

/**
 * Hook to get all brands
 */
export function useProductBrands() {
  return useQuery({
    queryKey: productLibraryKeys.brands(),
    queryFn: () => productLibraryService.getBrands(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to get featured products
 */
export function useFeaturedProducts(limit?: number) {
  return useQuery({
    queryKey: productLibraryKeys.featured(),
    queryFn: () => productLibraryService.getFeatured(limit),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to create a product
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateProductLibraryItemDto) => productLibraryService.create(dto),
    onSuccess: data => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: productLibraryKeys.all });
      toast.success('Product created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create product: ${error.message}`);
    },
  });
}

/**
 * Hook to update a product
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateProductLibraryItemDto }) =>
      productLibraryService.update(id, dto),
    onSuccess: data => {
      // Update the cache
      queryClient.setQueryData(productLibraryKeys.byId(data.id), data);
      queryClient.invalidateQueries({ queryKey: productLibraryKeys.all });
      toast.success('Product updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update product: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a product
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productLibraryService.delete(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: productLibraryKeys.byId(id) });
      queryClient.invalidateQueries({ queryKey: productLibraryKeys.all });
      toast.success('Product deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete product: ${error.message}`);
    },
  });
}
